'use client';

import Link from 'next/link';
import JsonLd from '@/components/JsonLd';
import IndustryCrossLinks from "@/components/landing/IndustryCrossLinks";
import ResearchBacking from "@/components/landing/ResearchBacking";
import Footer from "@/components/shared/Footer";
import PublicHeader from "@/components/shared/PublicHeader";

const recruitmentResearch = [
  {
    text: "AI-powered screening reduces time-to-hire by up to 50% by automating CV parsing, scoring, and initial candidate outreach.",
    source: "Industry Analysis 2025",
    url: "https://www.secondtalent.com/resources/ai-in-recruitment-statistics/",
  },
  {
    text: "Recruiters spend an average of 23 hours screening CVs for a single hire — 60% of which don&apos;t meet basic qualifications.",
    source: "LinkedIn Global Recruiting Trends 2024",
    url: "https://business.linkedin.com/talent-solutions/resources/talent-strategy/global-recruiting-trends",
  },
  {
    text: "Top candidates are off the market within 10 days. Agencies that respond within 1 hour are 7x more likely to place them.",
    source: "Glassdoor Hiring Timeline Report 2024",
    url: "https://www.glassdoor.com/research/",
  },
];

const recruitmentFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is an AI screening agent for recruitment?",
      acceptedAnswer: { "@type": "Answer", text: "An AI screening agent for recruitment is a chatbot that automatically parses CVs, scores candidates against job specifications, and schedules interviews. It responds to applicants instantly, 24/7, handling initial screening so your team focuses on qualified candidates." },
    },
    {
      "@type": "Question",
      name: "How does AI candidate screening work?",
      acceptedAnswer: { "@type": "Answer", text: "The AI uses retrieval-augmented generation (RAG) grounded in your job specifications, company information, and screening criteria. It parses incoming applications, matches candidates to requirements, and engages qualified applicants to schedule interviews. It only uses criteria you have explicitly provided." },
    },
    {
      "@type": "Question",
      name: "What are the limitations of AI agents for recruitment?",
      acceptedAnswer: { "@type": "Answer", text: "AI agents cannot make hiring decisions, assess cultural fit through subjective judgment, or replace human interview evaluation. They are designed for structured tasks like CV parsing, initial screening, and interview scheduling. Final hiring decisions are always made by your team." },
    },
  ],
};

const recruitmentSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Aditya Labs AI Recruitment Automation",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description: "AI-powered candidate sourcing and vetting system that screens applicants, qualifies candidates, and schedules interviews automatically.",
  url: "https://adityalabs.ai/recruitment",
  audience: { "@type": "BusinessAudience", audienceType: "Employment Agencies and Recruitment Firms" },
  provider: { "@type": "Organization", name: "Aditya Labs", url: "https://adityalabs.ai" },
  offers: { "@type": "Offer", price: "49", priceCurrency: "USD", description: "Plans from $49/month" },
};

export default function RecruitmentLandingPage() {
  return (
    <div className="min-h-screen bg-[#0B0F19] text-white font-sans overflow-x-hidden selection:bg-indigo-500/30">
      <JsonLd data={recruitmentSchema} />
      <JsonLd data={recruitmentFaqSchema} />

      {/* NAVBAR */}
      <PublicHeader variant="industry" accentColor="indigo" maxWidth="max-w-7xl" />

      {/* HERO SECTION */}
      <section className="relative pt-16 pb-24 lg:pt-32 lg:pb-40 overflow-hidden">

        {/* Ambient Background Glow (Indigo for Professional/Corporate) */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-indigo-600/15 rounded-full blur-[120px] -z-10 opacity-50"></div>

        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">

          {/* LEFT COLUMN: COPY */}
          <div className="space-y-10 relative z-10">

            {/* Urgency Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-bold tracking-wider uppercase animate-pulse">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
              </span>
              Average Time-to-Hire: 42 Days
            </div>

            {/* Headline */}
            <h1 className="text-5xl lg:text-7xl font-extrabold leading-tight tracking-tight">
              Your Recruiters Spend <br />
              60% of Their Day on <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-violet-400">CVs That Don&apos;t Qualify.</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg lg:text-xl text-gray-400 max-w-lg leading-relaxed">
              Stop drowning in CVs. Our AI screens candidates in <strong className="text-white">seconds</strong>, scores them against your job spec, and schedules interviews — so you only speak to qualified talent.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link href="/signup" className="flex items-center justify-center bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-xl shadow-indigo-900/40 transition-all transform hover:-translate-y-1">
                Automate Screening
              </Link>
              <Link href="/demo" className="flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/10 text-white px-8 py-4 rounded-xl font-medium backdrop-blur-sm transition-all">
                See Vetting Demo
              </Link>
            </div>

            {/* Trust/Guarantee */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-6 text-sm text-gray-500 dark:text-gray-400 pt-6 border-t border-gray-800/50">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                <span className="font-medium text-gray-300">Designed for GDPR — Candidate Data Protected</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                <span className="font-medium text-gray-300">&ldquo;First Placement&rdquo; Guarantee</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 max-w-md">If your first AI-screened candidate doesn&apos;t reach the interview stage, we&apos;ll optimize your scoring model for free. EEOC-aware scoring tested for disparate impact. Works with Bullhorn, Greenhouse &amp; Lever.</p>
            </div>
          </div>

          {/* RIGHT COLUMN: VISUAL PROOF (Candidate Screening) */}
          <div className="relative mx-auto lg:mx-0 w-full max-w-md">

            {/* Glass Container */}
            <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-3 rounded-[2.5rem] border border-gray-700/50 shadow-2xl backdrop-blur-xl relative z-10">
              <div className="bg-black rounded-[2rem] overflow-hidden border border-gray-800 h-[650px] flex flex-col relative">

                {/* Status Bar */}
                <div className="bg-navy/90 backdrop-blur-md p-4 flex items-center justify-between border-b border-gray-800 sticky top-0 z-20">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-xs font-bold shadow-lg">AI</div>
                    <div>
                      <div className="font-semibold text-white text-sm">Recruitment Screener</div>
                      <div className="text-[10px] text-indigo-400 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse"></span>
                        Screening Applicants
                      </div>
                    </div>
                  </div>
                </div>

                {/* Chat Area */}
                <div className="flex-1 p-5 space-y-6 overflow-y-auto bg-gray-950 scrollbar-hide">
                  <div className="text-center text-[10px] font-medium text-gray-600 dark:text-gray-400 uppercase tracking-widest my-4">New Application Received</div>

                  {/* System Event: New CV */}
                  <div className="flex justify-center">
                    <div className="bg-indigo-500/10 text-indigo-400 text-xs px-3 py-1 rounded-full border border-indigo-500/20">
                      New CV: Sarah Mitchell — Senior Developer
                    </div>
                  </div>

                  {/* Message 1 (AI Actions) */}
                  <div className="flex justify-start">
                    <div className="bg-gray-800 text-gray-300 px-4 py-2 rounded-xl text-xs italic border border-gray-700 w-full">
                      Parsing CV... Matching against job spec... Score: 92/100
                    </div>
                  </div>

                  {/* Message 2 (AI to Candidate) */}
                  <div className="flex justify-start">
                    <div className="bg-indigo-600 text-white px-4 py-3 rounded-2xl rounded-tl-sm max-w-[85%] text-sm leading-relaxed shadow-lg shadow-indigo-900/20">
                      Hi Sarah! Your profile is a strong match for the Senior Developer role at TechCorp. Are you available for a 30-min interview this week?
                    </div>
                  </div>

                  {/* Message 3 (Candidate) */}
                  <div className="flex justify-end">
                    <div className="bg-gray-800 text-gray-100 px-4 py-3 rounded-2xl rounded-tr-sm max-w-[85%] text-sm leading-relaxed shadow-md">
                      Yes! Thursday or Friday afternoon works for me.
                    </div>
                  </div>

                  {/* Message 4 (AI Confirmation) */}
                  <div className="flex justify-start">
                    <div className="bg-indigo-600 text-white px-4 py-3 rounded-2xl rounded-tl-sm max-w-[85%] text-sm leading-relaxed shadow-lg shadow-indigo-900/20">
                      Booked: Thursday 2:00 PM with the hiring manager. I&apos;ll send you the details now.
                    </div>
                  </div>

                  {/* Success Notification */}
                  <div className="flex justify-center my-6">
                    <div className="bg-green-500/10 text-green-400 text-xs font-semibold px-4 py-1.5 rounded-full border border-green-500/20 flex items-center gap-2">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                      Interview Booked &amp; Calendar Synced
                    </div>
                  </div>
                </div>

                {/* Input Area (Visual) */}
                <div className="p-4 bg-gray-900 border-t border-gray-800">
                  <div className="h-12 bg-gray-800 rounded-full w-full opacity-50 border border-gray-700"></div>
                </div>
              </div>
            </div>

            {/* Back Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-indigo-500/15 blur-[90px] -z-10 rounded-full"></div>
          </div>
        </div>
      </section>
      {/* Projected Impact */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">How AI Helps Recruitment Agencies</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-indigo-400 mb-2">Automated CV Screening</h3>
              <p className="text-sm text-gray-300 leading-relaxed mb-4">
                AI screens and scores every application against your job specs in seconds. Your recruiters focus on the top candidates instead of manually filtering hundreds of CVs.
              </p>
              <p className="text-xs text-gray-500">We are onboarding our first recruitment agencies now</p>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-blue-400 mb-2">Faster Candidate Engagement</h3>
              <p className="text-sm text-gray-300 leading-relaxed mb-4">
                AI engages qualified candidates within minutes of applying and can book interviews directly. Reduce time-to-hire by responding faster than your competitors.
              </p>
              <p className="text-xs text-gray-500">Based on recruitment industry response-time benchmarks</p>
            </div>
          </div>
          <p className="text-center text-xs text-gray-600 mt-6">AI scoring uses objective criteria only: skills match, experience level, location, and salary expectations. Weighted to YOUR job spec. Tested for disparate impact across protected classes.</p>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-10 px-6">
        <div className="max-w-2xl mx-auto text-center bg-indigo-900/20 border border-indigo-700/30 rounded-2xl p-8">
          <p className="text-white font-semibold text-lg mb-2">Plans start at $49/month</p>
          <p className="text-gray-400 text-sm mb-4">One faster placement at $25K commission covers years of subscriptions.</p>
          <Link href="/pricing" className="text-indigo-400 hover:text-indigo-300 text-sm font-medium underline">View all pricing plans →</Link>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6 bg-gray-900/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {[...recruitmentFaqSchema.mainEntity,
              { name: "Can I adjust the scoring criteria per role?", acceptedAnswer: { text: "Yes. Each job spec you upload becomes a unique scoring model. You define which skills are required vs. preferred, set minimum experience levels, and weight criteria by importance. For example, a Senior Developer role might weight 'React experience' at 30% and 'years of experience' at 20%. You can view and adjust these weights from your dashboard at any time." } },
              { name: "How is candidate data handled under GDPR?", acceptedAnswer: { text: "Candidate data is processed lawfully under GDPR Article 6(1)(f) — legitimate interest in recruitment. Candidates are informed via AI disclosure that their data is being processed by an automated system. Data is retained for the duration of the hiring process plus 6 months (configurable). Candidates can request deletion at any time, fulfilled within 72 hours. Data is never shared with third parties or used to train AI models." } },
              { name: "Does the AI support diversity hiring reporting?", acceptedAnswer: { text: "The AI scoring is designed to be bias-free — it evaluates only objective criteria (skills, experience, qualifications) without access to demographic data. For diversity reporting, your ATS integration captures self-reported demographic data separately from AI scoring, ensuring EEOC compliance. We provide a scoring audit log showing exactly how each candidate was evaluated." } },
            ].map((faq: { name: string; acceptedAnswer: { text: string } }, i: number) => (
              <div key={i} className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-3">{faq.name}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{faq.acceptedAnswer.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ResearchBacking stats={recruitmentResearch} />
      <IndustryCrossLinks current="recruitment" />
      <Footer />
    </div>
  );
}
